import React from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Info, Loader2 } from "lucide-react";

const ProcessingProgress = ({
  elapsedTime,
  estimatedTime,
  formatTime,
  getCurrentStage,
  getProgressPercent,
  PIPELINE_STAGES,
  hasAttachments = false,
}) => {
  const { t } = useLanguage();

  return (
    <Card className="border shadow-sm overflow-hidden" data-testid="processing-card">
      <CardContent className="pt-3 md:pt-4 pb-3 px-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">
                {getCurrentStage(elapsedTime, estimatedTime)?.label}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {t('query.consiliumWorking')}
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base md:text-lg font-mono font-semibold tabular-nums tracking-tight">
              {formatTime(elapsedTime)}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {elapsedTime < estimatedTime
                ? `~${formatTime(Math.max(0, estimatedTime - elapsedTime))} ${t('query.remaining')}`
                : t('query.almostDone')
              }
            </p>
          </div>
        </div>

        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${getProgressPercent(elapsedTime, estimatedTime)}%` }}
          />
        </div>

        {hasAttachments && (
          <div
            className="mt-2 flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1.5"
            data-testid="attachment-wait-note"
          >
            <Info className="mt-0.5 h-3 w-3 flex-shrink-0 text-primary" />
            <p className="text-[11px] text-muted-foreground leading-snug">
              {t('query.attachmentWaitNote')}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mt-2 px-0.5 gap-1">
          {PIPELINE_STAGES.map((stage, i) => {
            const currentStage = getCurrentStage(elapsedTime, estimatedTime);
            const isActive = stage.key === currentStage?.key;
            const isDone = PIPELINE_STAGES.indexOf(currentStage) > i;
            return (
              <div key={stage.key} className="flex items-center gap-1 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                  isDone ? 'bg-consensus' : isActive ? 'bg-primary' : 'bg-muted-foreground/20'
                }`} />
                <span className={`text-[9px] md:text-[10px] truncate ${
                  isDone ? 'text-consensus' : isActive ? 'text-foreground font-medium' : 'text-muted-foreground/50'
                }`}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProcessingProgress;
